import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";

import { DEFAULT_LANG, isSupportedLang } from "@/i18n";

/**
 * Default page title for the tenant / platform-marketing shell,
 * derived from the URL path.
 *
 * Two label sources:
 *   1. ``ROUTE_TITLE_I18N_KEYS`` — pre-translated entries reused from
 *      existing i18n namespaces (``common:nav.*``, ``common:footer.*``).
 *   2. ``ROUTE_TITLE_INLINE`` — small inline CS/EN dictionary for
 *      routes whose label doesn't (yet) have an i18n key. Keeping
 *      these inline avoids spraying single-use keys across translation
 *      files for shell-level fallback titles; pages that need a richer
 *      title (with an id, a name, etc.) call ``useSeo`` directly and
 *      override the layout default.
 *
 * Pages with an explicit ``useSeo`` (Pricing, Materials, Account,
 * SignIn, QuoteDetail, PublicOrg, etc.) override this default —
 * page-level effects run after the layout's. See ``priority: "layout"``
 * in ``apps/web/src/lib/seo.ts``.
 *
 * Unknown paths return an empty string → the composed ``<title>``
 * collapses to just the brand name.
 */
const ROUTE_TITLE_I18N_KEYS: ReadonlyArray<readonly [RegExp, string]> = [
  [/^\/cart\/?$/, "common:nav.cart"],
  // The `pages_visibility`-gated public pages. Each one *does* call
  // ``useSeo`` itself — but only once it renders, and
  // ``RequirePageEnabled`` renders nothing at all while the branding
  // query that decides its visibility is in flight. For the whole
  // duration of that fetch the layout default was the only title
  // writer, and it had no entry for these paths, so every one of them
  // sat on the bare brand name — the exact degenerate-title bug
  // ``e2e/specs/seo-titles.spec.ts`` exists to catch. It went unseen
  // because the suite had never actually run in CI.
  //
  // The labels agree with each page's own ``seo.title``, so the title
  // no longer changes when the gate resolves.
  [/^\/pricing\/?$/, "common:nav.pricing"],
  [/^\/materials\/?$/, "common:nav.materials"],
  [/^\/contact\/?$/, "common:nav.contact"],
  [/^\/how-it-works\/?$/, "common:nav.howItWorks"],
  // ``legal:subprocessors.title`` — the page's own H1. It used to
  // point at ``common:footer.terms``, i.e. every visitor and every
  // crawler saw the subprocessor list titled "Obchodní podmínky".
  // Harmless while the page was client-rendered and unlisted; it is
  // now prerendered and in the sitemap, so the wrong title is what
  // gets indexed.
  [/^\/legal\/subprocessors\/?$/, "legal:subprocessors.title"],
];

interface InlineLabel {
  cs: string;
  en: string;
}

const ROUTE_TITLE_INLINE: ReadonlyArray<readonly [RegExp, InlineLabel]> = [
  [/^\/track\/?$/, { cs: "Sledování zásilky", en: "Track shipment" }],
  [/^\/quote\/[^/]+\/?$/, { cs: "Nabídka", en: "Quote" }],
  [/^\/quote-batch\/[^/]+\/?$/, { cs: "Nabídka", en: "Quote" }],
  [/^\/quotes\/by-id\/[^/]+\/?$/, { cs: "Nabídka", en: "Quote" }],
  [/^\/buyer\/[^/]+\/?$/, { cs: "Přehled nákupčího", en: "Buyer dashboard" }],
  [
    /^\/buyer\/[^/]+\/addresses\/?$/,
    { cs: "Adresy", en: "Addresses" },
  ],
  [
    /^\/buyer\/[^/]+\/reports\/?$/,
    { cs: "Reporty", en: "Reports" },
  ],
  [
    /^\/buyer\/[^/]+\/invoices\/?$/,
    { cs: "Faktury", en: "Invoices" },
  ],
  [
    /^\/buyer\/[^/]+\/capacity\/?$/,
    { cs: "Nákup kapacity", en: "Capacity purchase" },
  ],
  [
    /^\/quality-report\/[^/]+\/?$/,
    { cs: "Kontrola kvality", en: "Quality report" },
  ],
  [/^\/onboarding\/provider(?:\/.*)?$/, {
    cs: "Onboarding výrobce",
    en: "Provider onboarding",
  }],
  [/^\/onboarding\/customer-org\/?$/, {
    cs: "Onboarding zákazníka",
    en: "Customer onboarding",
  }],
];

export function useCustomerPageTitle(): string {
  const { t } = useTranslation();
  const location = useLocation();
  const { lang: langParam } = useParams<{ lang: string }>();
  const lang = isSupportedLang(langParam) ? langParam : DEFAULT_LANG;

  const pathAfterLang = location.pathname.replace(
    new RegExp(`^/${lang}/?`),
    "/",
  );

  return useMemo(() => {
    for (const [pattern, key] of ROUTE_TITLE_I18N_KEYS) {
      if (pattern.test(pathAfterLang)) {
        return t(key, { defaultValue: "" });
      }
    }
    for (const [pattern, label] of ROUTE_TITLE_INLINE) {
      if (pattern.test(pathAfterLang)) {
        return lang === "en" ? label.en : label.cs;
      }
    }
    return "";
  }, [pathAfterLang, lang, t]);
}
